import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { MessageSquare, Trash2, Reply, Send, X } from 'lucide-react';
import { commentApi } from '../../utils/api';

function formatWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('ne-NP', { year: 'numeric', month: 'short', day: 'numeric' });
}

function AdminComments() {
  const [comments, setComments] = useState([]);
  const [catFilter, setCatFilter] = useState('all');
  const [newsFilter, setNewsFilter] = useState('all');
  const [replyFor, setReplyFor] = useState(null); // comment id
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await commentApi.getAll();
      setComments(res.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('कम्मेन्टहरू लोड गर्न असफल।');
    } finally {
      setLoading(false);
    }
  };

  // Unique categories ra news list (filter dropdown ko lagi)
  const categories = useMemo(() => {
    const map = new Map();
    comments.forEach((c) => {
      if (c.category_slug && !map.has(c.category_slug)) {
        map.set(c.category_slug, c.category_name);
      }
    });
    return Array.from(map, ([slug, name]) => ({ slug, name }));
  }, [comments]);

  const newsList = useMemo(() => {
    const map = new Map();
    comments.forEach((c) => {
      if (c.news_id && !map.has(c.news_id)) {
        map.set(c.news_id, { id: c.news_id, title: c.news_title || 'मेटिएको समाचार', image: c.news_image });
      }
    });
    return Array.from(map.values());
  }, [comments]);

  const filtered = comments.filter((c) => {
    if (catFilter !== 'all' && c.category_slug !== catFilter) return false;
    if (newsFilter !== 'all' && String(c.news_id) !== String(newsFilter)) return false;
    return true;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('यो कम्मेन्ट मेटाउन निश्चित हुनुहुन्छ?')) return;
    try {
      await commentApi.delete(id);
      setComments((cs) => cs.filter((c) => c.id !== id));
      toast.success('कम्मेन्ट मेटियो।');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('मेट्न असफल।');
    }
  };

  const openReply = (c) => {
    setReplyFor(c.id);
    setReplyText(c.admin_reply || '');
  };

  const cancelReply = () => {
    setReplyFor(null);
    setReplyText('');
  };

  const saveReply = async (id) => {
    if (!replyText.trim()) {
      toast.error('जवाफ लेख्नुहोस्।');
      return;
    }
    setSendingReply(true);
    try {
      const res = await commentApi.reply(id, replyText.trim());
      setComments((cs) => cs.map((c) => (c.id === id ? res.data : c)));
      toast.success('जवाफ सुरक्षित भयो।');
      cancelReply();
    } catch (error) {
      console.error('Error saving reply:', error);
      toast.error('जवाफ पठाउन असफल।');
    } finally {
      setSendingReply(false);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const selectCls = 'border border-line rounded-lg px-3 py-2.5 text-[13px] outline-none bg-white focus:border-crimson transition-colors';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <MessageSquare size={22} className="text-crimson" /> कम्मेन्ट व्यवस्थापन
          <span className="text-[13px] font-semibold text-muted">({filtered.length})</span>
        </h2>
      </div>

      {/* Filters — category wise / news title wise */}
      <div className="flex items-center gap-3 flex-wrap mb-5 bg-white border border-line rounded-xl p-4">
        <label className="flex items-center gap-2 text-[12.5px] font-bold text-ink-soft">
          श्रेणी:
          <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={selectCls}>
            <option value="all">सबै</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[12.5px] font-bold text-ink-soft">
          समाचार:
          <select value={newsFilter} onChange={(e) => setNewsFilter(e.target.value)} className={`${selectCls} max-w-[280px]`}>
            <option value="all">सबै</option>
            {newsList.map((n) => (
              <option key={n.id} value={n.id}>{n.title}</option>
            ))}
          </select>
        </label>
        {(catFilter !== 'all' || newsFilter !== 'all') && (
          <button
            onClick={() => { setCatFilter('all'); setNewsFilter('all'); }}
            className="inline-flex items-center gap-1 text-[12px] font-bold text-crimson hover:underline"
          >
            <X size={13} /> फिल्टर हटाउनुहोस्
          </button>
        )}
      </div>

      {/* Comment cards */}
      <div className="flex flex-col gap-3">
        {filtered.map((c) => (
          <div key={c.id} className="bg-white border border-line rounded-xl p-4 transition-shadow hover:shadow-md">
            <div className="flex items-start gap-3.5">
              {/* News mini photo */}
              <img
                src={c.news_image || 'https://via.placeholder.com/64x64?text=K'}
                alt=""
                className="w-14 h-14 rounded-lg object-cover bg-paper-tint flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                {/* News title + category */}
                <p className="text-[12px] font-bold text-blue truncate">{c.news_title || 'मेटिएको समाचार'}</p>
                {c.category_name && <span className="inline-block text-[10.5px] font-bold text-muted bg-paper-tint rounded-full px-2 py-0.5 mt-0.5">{c.category_name}</span>}

                {/* Commenter + text */}
                <div className="mt-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-bold text-ink">{c.name}</p>
                    <p className="text-[13.5px] text-ink-soft leading-relaxed break-words">{c.comment}</p>
                    <p className="text-[11px] text-muted mt-1">{formatWhen(c.created_at)}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      aria-label="जवाफ दिनुहोस्"
                      title="जवाफ दिनुहोस्"
                      onClick={() => (replyFor === c.id ? cancelReply() : openReply(c))}
                      className={`p-2 rounded-full border transition-colors ${
                        replyFor === c.id ? 'bg-crimson text-white border-crimson' : 'border-line text-ink-soft hover:text-crimson hover:border-crimson'
                      }`}
                    >
                      <Reply size={14} />
                    </button>
                    <button
                      type="button"
                      aria-label="मेट्नुहोस्"
                      title="मेट्नुहोस्"
                      onClick={() => handleDelete(c.id)}
                      className="p-2 rounded-full border border-line text-ink-soft hover:text-white hover:bg-crimson hover:border-crimson transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Existing admin reply — रातो र bold */}
                {c.admin_reply && replyFor !== c.id && (
                  <div className="mt-2.5 bg-red-50 border-l-4 border-crimson rounded-r-lg px-3 py-2">
                    <p className="text-[10.5px] font-bold text-crimson uppercase tracking-wide mb-0.5">एडमिनको जवाफ</p>
                    <p className="text-[13px] font-bold text-crimson leading-relaxed">{c.admin_reply}</p>
                  </div>
                )}

                {/* Reply editor */}
                {replyFor === c.id && (
                  <div className="mt-3 flex flex-col gap-2">
                    <textarea
                      rows={2}
                      autoFocus
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="एडमिनको जवाफ लेख्नुहोस्..."
                      className="w-full border border-line rounded-lg px-3 py-2 text-[13px] outline-none resize-y focus:border-crimson"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={sendingReply}
                        onClick={() => saveReply(c.id)}
                        className="inline-flex items-center gap-1.5 bg-crimson text-white text-[12.5px] font-bold px-4 py-2 rounded-full hover:bg-crimson-dark disabled:opacity-60 transition-colors"
                      >
                        <Send size={13} /> {sendingReply ? 'पठाउँदै...' : 'जवाफ पठाउनुहोस्'}
                      </button>
                      <button type="button" onClick={cancelReply} className="text-[12.5px] font-semibold text-muted hover:text-ink px-2">
                        रद्द
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-muted">
            <MessageSquare size={44} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">कुनै कम्मेन्ट छैन।</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminComments;